import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useCompany } from '../../hooks/useCompany';
import { ModeToggle } from '../mode-toggle';

/**
 * The shell's brand + controls row: company logo, notification bell, theme
 * switcher.
 *
 * Rendered twice — in the desktop sidebar header and in the mobile top bar —
 * from one component, so the two cannot drift apart the way AppShell's old
 * duplicated desktop/mobile trees did.
 */

interface ShellBrandBarProps {
  /** Fires with the clicked element so the caller can anchor the bell popover. */
  onBellClick: (el: HTMLButtonElement) => void;
  badgeCount: number;
}

export default function ShellBrandBar({ onBellClick, badgeCount }: ShellBrandBarProps) {
  const company = useCompany();
  const [imgError, setImgError] = useState(false);

  const showLogo = !!company.logoUrl && !imgError;

  const brandMark = showLogo ? (
    <span
      data-shell-logo="image"
      className="flex max-h-9 max-w-full items-center overflow-hidden bg-white px-1.5 py-0.5"
    >
      <img
        src={company.logoUrl!}
        alt={company.name}
        onError={() => setImgError(true)}
        className="block max-h-8 max-w-full object-contain object-left"
      />
    </span>
  ) : (
    <span
      data-shell-logo="monogram"
      className="flex size-8 items-center justify-center overflow-hidden bg-primary"
    >
      <span className="font-heading text-13 font-bold text-primary-foreground">
        {company.short}
      </span>
    </span>
  );

  return (
    <div className="flex h-14 flex-shrink-0 items-center gap-2.5 px-4">
      {/* The brand is navigation to the dashboard, not an upload control. */}
      <div className="flex min-w-0 flex-1 items-center">
        <Link
          to="/dashboard"
          aria-label={`Go to dashboard for ${company.name}`}
          title="Dashboard"
          className="flex max-h-8 min-w-0 max-w-full items-center focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none"
        >
          {brandMark}
        </Link>
      </div>

      <div className="flex flex-shrink-0 items-center gap-2.5">
        <button
          onClick={(e) => onBellClick(e.currentTarget)}
          className="relative flex size-8 flex-shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {badgeCount > 0 && (
            <span className="absolute top-0.5 right-0.5 flex size-4 items-center justify-center rounded-full bg-destructive font-body text-[9px] font-bold text-primary-foreground">
              {Math.min(badgeCount, 99)}
            </span>
          )}
        </button>

        <ModeToggle compact />
      </div>
    </div>
  );
}
